import { EntityType } from "../../common/Common.types.ts";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useRelatedTasksTab } from "./RelatedTasksTab.hooks.ts";
import {
    DotLegalButton,
    DotLegalDeleteDialog,
    DotLegalEmptyState,
    DotLegalLink,
    DotLegalOverflowMenu,
    DotLegalOverflowMenuItem,
    DotLegalPaper,
    ITableHeader,
} from "@dotlegal/dotlegal-ui-components";
import { RelatedTasksTableModel, translateTaskOrigin } from "./RelatedTasksTab.types.ts";
import { Box, TableCell, TableRow } from "@mui/material";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import AddTaskButton from "../addTask/addTaskButton/AddTaskButton.tsx";
import { Trans } from "react-i18next";
import { useUrlProvider } from "../../useUrlProvider.ts";
import DeleteTaskDialog from "../deleteTaskDialog/DeleteTaskDialog.tsx";
import RelateToExistingsTasksDialog from "./relateToExistingTasksDialog/RelateToExistingTasksDialog.tsx";
import AnnualWheelActivityStatusChip from "../../annualWheel/annualWheelActivityStatusChip/AnnualWheelActivityStatusChip.tsx";
import DotLegalTableWithFilterBar from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";

export interface RelatedTasksTabProps {
    forEntityId: string;
    forEntityType: EntityType;
    forEntityName: string;
    customApiUrl?: string;
    additionalHeaderTypeTableHeader?: string;
    showTaskOrigin?: boolean;
    onUpdate?: () => void;
    orderBy?: keyof RelatedTasksTableModel;
}

function RelatedTasksTab(props: RelatedTasksTabProps) {
    const { translateString, translateDate } = useTranslation();
    const { permissions } = useUserContext();
    const { getTaskManagementUrlForTask } = useUrlProvider();

    const {
        isLoading,
        refetchTasks,
        data,
        selectedRelationToRemove,
        setSelectedRelationToRemove,
        removeRelation,
        openTask,
        selectedTaskToDelete,
        setSelectedTaskToDelete,
        setShowAddExistingTasksDialog,
        showAddExistingTasksDialog,
        openActivityInReadMode,
        mapSearchFilter,
        getFilterDataOptions,
        hasRelatedTasks,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useRelatedTasksTab(props);

    const showAdditionalColumns = props.customApiUrl && props.additionalHeaderTypeTableHeader;

    function getHeaders() {
        let headers = Array<ITableHeader<RelatedTasksTableModel>>();
        headers.push({ property: "customId", text: translateString("id") });
        headers.push({ property: "title", text: translateString("task") });
        headers.push({ property: "taskState", text: translateString("status") });
        headers.push({ property: "responsible", text: translateString("responsible") });
        headers.push({ property: "deadline", text: translateString("deadline") });

        if (showAdditionalColumns) {
            headers.push({ property: "entityName", text: props.additionalHeaderTypeTableHeader! });
        }

        if (props.showTaskOrigin) {
            headers.push({ property: "taskOrigin", text: translateString("taskType") });
        }

        return headers;
    }

    function refetchData() {
        if (props.onUpdate) {
            props.onUpdate();
        }

        refetchTasks();
    }

    function getOverflowMenu(row: RelatedTasksTableModel) {
        let menuItems: Array<DotLegalOverflowMenuItem> = [
            {
                menuItem: translateString("openTask"),
                onClick: () => {
                    openTask(row.id);
                },
            },
            {
                menuItem: translateString("viewInTaskManagement"),
                href: getTaskManagementUrlForTask(new Date(row.deadline).getMonth() + 1, row.id),
            },
        ];

        // Ad hoc tasks have no activity behind them, so there is nothing to show.
        if (row.annualWheelActivityId) {
            menuItems.push({
                menuItem: translateString("seeActivity"),
                onClick: () => {
                    openActivityInReadMode(row.annualWheelActivityId!);
                },
            });
        }

        if (row.isRelation) {
            menuItems.push({
                menuItem: translateString("removeRelation"),
                onClick: () => {
                    setSelectedRelationToRemove(row);
                },
            });
        }

        if (permissions.taskPermissions.delete) {
            menuItems.push({
                menuItem: translateString("deleteTask"),
                onClick: () => {
                    setSelectedTaskToDelete(row);
                },
            });
        }

        return <DotLegalOverflowMenu items={menuItems} />;
    }

    return (
        <DotLegalPaper>
            {hasRelatedTasks ? (
                <DotLegalTableWithFilterBar
                    filterBar={{
                        isLoading: isLoading,
                        filterData: getFilterDataOptions(),
                        fetchFilterData: true,
                        onMenuClose: (options) => mapSearchFilter(options),
                        onRemove: (options) => mapSearchFilter(options),
                        pinnedFilterIds: pinnedFilterIds,
                        onPinnedFilterIdsChange: onPinnedFilterIdsChange,
                    }}
                    headers={getHeaders()}
                    rowsPerPage={30}
                    clickableRows
                    hasOverflowMenu
                    defaultOrderBy={props.orderBy ?? "title"}
                    defaultOrder={"asc"}
                    isLoading={isLoading}
                    data={data}
                    emptyText={translateString(hasActiveFilters ? "noDataMatchingCriteria" : "noRelatedTasks")}
                    renderRow={(row, i) => (
                        <TableRow key={i} onClick={() => openTask(row.id)}>
                            <TableCell>{row.customId}</TableCell>
                            <TableCell>
                                <Box sx={{ "& button": { font: "inherit", fontWeight: 700 } }}>
                                    <DotLegalLink linkColor="primary" onClick={() => openTask(row.id)}>
                                        {row.title}
                                    </DotLegalLink>
                                </Box>
                            </TableCell>

                            <TableCell>
                                <AnnualWheelActivityStatusChip status={row.taskState} value={row.stateString} />
                            </TableCell>
                            <TableCell>{row.responsible?.name}</TableCell>
                            <TableCell>{translateDate(row.deadline)}</TableCell>

                            {showAdditionalColumns && (
                                <TableCell>
                                    {row.additionalEntityInfo?.additionalEntityName} {row.additionalEntityInfo ? `(${row.entityTypeString})` : undefined}
                                </TableCell>
                            )}

                            {props.showTaskOrigin && <TableCell>{translateTaskOrigin(row.taskOrigin, translateString)}</TableCell>}

                            <TableCell align={"right"}>{getOverflowMenu(row)}</TableCell>
                        </TableRow>
                    )}
                />
            ) : (
                <DotLegalEmptyState text={translateString("noRelatedTasks")} />
            )}

            {permissions.taskPermissions.create && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <AddTaskButton
                        relationInformation={{
                            fromEntityId: props.forEntityId,
                            fromEntityType: props.forEntityType,
                            toEntityType: EntityType.Task,
                        }}
                    />
                    <DotLegalButton buttonType={"secondary"} onClick={() => setShowAddExistingTasksDialog(true)}>
                        {translateString("relateToExistingTask")}
                    </DotLegalButton>
                </Box>
            )}

            {showAddExistingTasksDialog && (
                <RelateToExistingsTasksDialog
                    entityType={props.forEntityType}
                    entityId={props.forEntityId}
                    onDialogClose={() => setShowAddExistingTasksDialog(false)}
                    onSaved={() => {
                        refetchData();
                        setShowAddExistingTasksDialog(false);
                    }}
                />
            )}

            {selectedRelationToRemove !== undefined && (
                <DotLegalDeleteDialog
                    headerText={translateString("removeRelation")}
                    deleteBtnText={"Delete"}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setSelectedRelationToRemove(undefined)}
                    onOkClick={() => removeRelation(selectedRelationToRemove.relationId)}
                    open
                    alertBodyText={<Trans i18nKey="removeRelationWarning" values={{ from: props.forEntityName, to: selectedRelationToRemove.title }} />}
                    warningTranslation={translateString("warning")}
                />
            )}

            {selectedTaskToDelete && (
                <DeleteTaskDialog
                    taskId={selectedTaskToDelete.id}
                    taskName={selectedTaskToDelete.title}
                    onCloseDialog={() => setSelectedTaskToDelete(undefined)}
                    onDeleteClick={() => {
                        refetchData();
                        setSelectedTaskToDelete(undefined);
                    }}
                />
            )}
        </DotLegalPaper>
    );
}

export default RelatedTasksTab;
